import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  ImageList,
  ImageListItem,
  Tooltip,
  Chip
} from '@mui/material';
import { CheckCircleOutline, ErrorOutline, SkipNext } from '@mui/icons-material';
import {
  PhotoLibrary,
  CloudDownload,
  CheckCircle,
  Info,
  OpenInNew
} from '@mui/icons-material';
// CheckCircleOutline, ErrorOutline, SkipNext imported above
import googlePhotosService from '../services/googlePhotosService';
import { getGoogleClientIdDiagnostics, getGooglePhotosApiUrl } from '../config/runtimeConfig';
import AuthenticatedImage from './AuthenticatedImage';

const GooglePhotosDialog = ({ open, onClose, onPhotosSelected, onPhotosImported, currentSession }) => {
  const googleClientIdDiagnostics = getGoogleClientIdDiagnostics();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [importProgress, setImportProgress] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  
  // Thumbnails populated as each photo finishes uploading
  const [completedThumbnails, setCompletedThumbnails] = useState([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const abortControllerRef = useRef(null);
  const currentJobIdRef = useRef(null); // Stores the jobId received in 'started' SSE event

  useEffect(() => {
    if (open) {
      console.log('[GooglePhotosDialog] Opened with diagnostics', googleClientIdDiagnostics);
      console.log('[GooglePhotosDialog] Google Photos API URL', getGooglePhotosApiUrl());
    }
  }, [open, googleClientIdDiagnostics]);

  // Progress tracking state
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    completedItems: 0,
    totalItems: 0,
    percentage: 0,
    currentItem: '',
    statusMessage: '',
    errors: [],
    skipped: [],
    completed: false
  });

  // Process selected photos from Google Photos API response
  const processSelectedPhotos = async (pickedMediaItems) => {
    try {
      setLoading(true);
      setImportProgress('Processing imported photos...');
      
      console.log('🔄 processSelectedPhotos called with:', pickedMediaItems);
      console.log('🔍 Debug - pickedMediaItems type:', typeof pickedMediaItems);
      console.log('🔍 Debug - pickedMediaItems keys:', pickedMediaItems ? Object.keys(pickedMediaItems) : 'null/undefined');
      
      console.log(`✅ Received ${pickedMediaItems.mediaItems?.length || 0} photos from Google Photos picker`);
      console.log(`📋 Current session for uploads:`, currentSession?.uniqueId || 'No session');
      
      // Check if photos were automatically uploaded
      const uploadedFiles = pickedMediaItems.uploadedFiles || [];
      const mediaItems = pickedMediaItems.mediaItems || [];
      
      console.log(`📥 Auto-uploaded files count: ${uploadedFiles.length}`);
      console.log(`📋 Media items count: ${mediaItems.length}`);
      
      if (uploadedFiles.length > 0) {
        console.log('📁 Taking uploadedFiles path:', uploadedFiles.length, 'files');
        setImportProgress(`Successfully imported ${uploadedFiles.length} photos`);
        
        // Show success message
        alert(`Successfully imported ${uploadedFiles.length} photos to your media library!`);
        
        // Trigger a refresh of the media library if there's a callback
        if (onPhotosImported && typeof onPhotosImported === 'function') {
          onPhotosImported({ uploadedFiles, errors: [], skipped: [], total: uploadedFiles.length });
        }
        
        // Close the dialog
        onClose();
      } else if (mediaItems.length > 0) {
        // Media items were retrieved but need to be uploaded with progress tracking
        console.log('🚀 Taking mediaItems path - Starting progressive upload for', mediaItems.length, 'photos');
        setImportProgress('Starting upload with progress tracking...');
        
        // Use the progressive upload method
        const accessToken = googlePhotosService.accessToken;
        if (!accessToken) {
          throw new Error('Access token not available for upload');
        }
        
        console.log('✅ About to call uploadPhotosWithProgress');
        await uploadPhotosWithProgress(mediaItems, accessToken);
      } else {
        console.log('❌ No photos found in either path');
        setImportProgress('No photos were selected');
        setError('No photos were selected from Google Photos');
      }
      
    } catch (error) {
      console.error('❌ Error processing photos:', error);
      setImportProgress('Error processing photos');
      setError(error.message);
      alert(`Failed to process photos: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle testing backend connection
  const handleTestConnection = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔧 Testing backend Google Photos API connection...');
      
      // First ensure we're initialized
      if (!googlePhotosService.isInitialized) {
        console.log('🔧 Initializing Google Photos service...');
        await googlePhotosService.initialize();
      }
      
      // Then ensure we're authenticated with Google
      if (!googlePhotosService.accessToken) {
        console.log('🔧 Requesting Google Photos access...');
        await googlePhotosService.requestAccess();
      }
      
      // Test the backend connection
      const result = await googlePhotosService.testBackendConnection();
      
      console.log('✅ Backend connection test successful:', result);
      setError(`✅ Backend connection successful! ${result.message || 'API access verified'}`);
      
    } catch (err) {
      console.error('❌ Backend connection test failed:', err);
      setError(`Backend connection failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening Google Photos Picker
  const handleOpenPhotoPicker = async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedPhotos([]);
      setIsPolling(true);
      
      console.log('📸 Starting Google Photos Picker flow via backend proxy...');
      console.log('📋 Using session ID for uploads:', currentSession?.uniqueId);
      
      // Start the picker flow - this will open a new window and start polling
      const mediaItems = await googlePhotosService.startPickerFlow(currentSession?.uniqueId);
      
      console.log('🔍 Debug - Received mediaItems:', mediaItems);
      console.log('🔍 Debug - MediaItems type:', typeof mediaItems);
      console.log('🔍 Debug - MediaItems keys:', mediaItems ? Object.keys(mediaItems) : 'null/undefined');
      
      // Check for photos in the correct property name
      const selectedPhotos = mediaItems?.mediaItems || mediaItems?.pickedMediaItems || [];
      
      console.log('🔍 Debug - selectedPhotos:', selectedPhotos);
      console.log('🔍 Debug - selectedPhotos length:', selectedPhotos?.length);
      
      if (selectedPhotos && selectedPhotos.length > 0) {
        console.log('✅ User selected photos from Google Photos:', selectedPhotos.length);
        console.log('🔍 Debug - First photo structure:', selectedPhotos[0]);
        setSelectedPhotos(selectedPhotos);
        
        // Process and upload the selected photos directly
        // Pass the full mediaItems object (which contains uploadedFiles and mediaItems)
        console.log('🚀 About to call processSelectedPhotos with:', mediaItems);
        await processSelectedPhotos(mediaItems);
        
      } else {
        console.log('ℹ️ No photos were selected');
        setError('No photos were selected. Please try again.');
      }
    } catch (err) {
      console.error('❌ Google Photos import error:', err);
      setError(`Failed to import photos: ${err.message}`);
    } finally {
      setLoading(false);
      setIsPolling(false);
      setImportProgress(null);
    }
  };

  // Function to upload photos with progress tracking
  const uploadPhotosWithProgress = async (mediaItems, accessToken) => {
    const correlationId = crypto.randomUUID();
    console.log('🚀 Starting progressive upload for', mediaItems.length, 'photos | albumSessionId:', currentSession?.uniqueId, '| correlationId:', correlationId);
    console.log('📊 Initial uploadProgress state before setting isUploading to true | correlationId:', correlationId);

    setCompletedThumbnails([]);
    setIsCancelling(false);
    currentJobIdRef.current = null;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setUploadProgress({
      isUploading: true,
      completedItems: 0,
      totalItems: mediaItems.length,
      percentage: 0,
      currentItem: '',
      statusMessage: 'Starting upload...',
      errors: [],
      skipped: [],
      completed: false
    });

    console.log('✅ uploadProgress.isUploading set to true - progress bar should be visible now');

    try {
      const API_BASE_URL = getGooglePhotosApiUrl();

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/upload-with-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        signal: abortController.signal,
        body: JSON.stringify({
          // mediaItems are cached server-side after /media is called;
          // do NOT send them here to avoid Azure ingress body-size limits.
          accessToken: accessToken,
          userSessionId: currentSession?.uniqueId,
          correlationId,
        })
      });

      if (!response.ok) {
        // Parse structured error body (e.g. 409 already_running)
        let errMsg = `Upload failed: ${response.statusText}`;
        try {
          const errBody = await response.json();
          if (errBody?.error) errMsg = errBody.error;
        } catch { /* ignore parse failure */ }
        throw new Error(errMsg);
      }

      // Backend starts the upload asynchronously and returns a jobId immediately.
      // Frontend polls /upload-progress/:jobId until the job finishes.
      const startData = await response.json();
      const uploadJobId = startData.jobId;
      currentJobIdRef.current = uploadJobId;
      console.log('🎬 Upload started | jobId:', uploadJobId, '| correlationId:', correlationId, '| total:', startData.total);

      // Update initial total from backend response
      setUploadProgress(prev => ({ ...prev, totalItems: startData.total || prev.totalItems }));

      // Poll for progress every 1.5 s until the job finishes
      let pollDone = false;
      while (!pollDone) {
        await new Promise(resolve => setTimeout(resolve, 1500));

        const progressResp = await fetch(`${API_BASE_URL}/upload-progress/${uploadJobId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: abortController.signal,
        });

        if (!progressResp.ok) {
          if (progressResp.status === 404) {
            // Job cleaned up — completed and 5-min window elapsed, or a restart wiped memory.
            // Treat as success if we uploaded at least some photos; otherwise show warning.
            console.warn('⚠️ Job not found during poll — treating as completed');
            const savedCount = uploadProgress?.completedItems || 0;
            if (savedCount > 0 && onPhotosImported) {
              await onPhotosImported({ uploadedFiles: [], errors: [], skipped: [], total: savedCount });
            }
            setUploadProgress(prev => ({
              ...prev,
              percentage: 100,
              statusMessage: savedCount > 0
                ? `Upload finished — ${savedCount} photo${savedCount !== 1 ? 's' : ''} processed`
                : 'Upload status unavailable — please check your image library',
              completed: true,
            }));
            setTimeout(() => {
              onClose();
              setUploadProgress({ isUploading: false, completedItems: 0, totalItems: 0, percentage: 0, currentItem: '', statusMessage: '', errors: [], skipped: [], completed: false });
              setCompletedThumbnails([]);
              setIsCancelling(false);
            }, 2000);
            break;
          }
          throw new Error(`Progress poll failed: ${progressResp.statusText}`);
        }

        const pollData = await progressResp.json();
        const p = pollData.progress || {};
        console.log('📊 Poll:', pollData.status, p.type, `${p.percentage}%`);

        // Update progress UI for running states
        if (p.type === 'progress') {
          setUploadProgress(prev => ({
            ...prev,
            completedItems: p.processed,
            totalItems: p.total,
            percentage: p.percentage,
            currentItem: p.currentItem || '',
            statusMessage: `Uploading ${p.processed}/${p.total} photos`
          }));
        } else if (p.type === 'item-complete') {
          setUploadProgress(prev => ({
            ...prev,
            completedItems: p.processed,
            percentage: p.percentage,
            currentItem: p.currentItem || '',
            statusMessage: `Completed ${p.processed}/${p.total} photos`,
            errors: p.status === 'error' ? [...prev.errors, { item: p.currentItem, error: p.error }] : prev.errors,
            skipped: p.status === 'skipped' ? [...prev.skipped, { item: p.currentItem, reason: p.reason }] : prev.skipped
          }));
          if (p.imageId) {
            setCompletedThumbnails(prev => [...prev, {
              filename: p.currentItem || 'Photo',
              imageId: p.imageId,
              status: p.status
            }]);
          }
        }

        // Handle terminal states
        if (pollData.status === 'complete') {
          const r = pollData.result || {};
          const completedFiles = r.uploadedFiles || [];
          const finalErrors = r.errors || [];
          const finalSkipped = r.skipped || [];
          setUploadProgress(prev => ({
            ...prev,
            completedItems: startData.total,
            totalItems: startData.total,
            percentage: 100,
            statusMessage: `Successfully uploaded ${completedFiles.length} photos!`,
            completed: true,
            errors: finalErrors,
            skipped: finalSkipped
          }));
          console.log('✅ Upload complete | uploaded:', completedFiles.length, '| errors:', finalErrors.length, '| skipped:', finalSkipped.length, '| correlationId:', correlationId);

          if (onPhotosImported) {
            await onPhotosImported({ uploadedFiles: completedFiles, errors: finalErrors, skipped: finalSkipped, total: startData.total });
          }
          setTimeout(() => {
            onClose();
            setUploadProgress({ isUploading: false, completedItems: 0, totalItems: 0, percentage: 0, currentItem: '', statusMessage: '', errors: [], skipped: [], completed: false });
            setCompletedThumbnails([]);
            setIsCancelling(false);
          }, 2000);
          pollDone = true;
        } else if (pollData.status === 'cancelled') {
          const r = pollData.result || {};
          const saved = (r.uploadedFiles || []).length;
          setUploadProgress(prev => ({
            ...prev,
            isUploading: false,
            statusMessage: `Upload cancelled — ${saved} photo${saved !== 1 ? 's' : ''} saved`,
            errors: r.errors || prev.errors,
            skipped: r.skipped || prev.skipped
          }));
          setIsCancelling(false);
          pollDone = true;
        } else if (pollData.status === 'error') {
          throw new Error(p.error || 'Upload failed on the server');
        }
      }

    } catch (uploadError) {
      if (uploadError.name === 'AbortError') {
        console.log('✋ Upload cancelled by user');
        setUploadProgress(prev => ({
          ...prev,
          isUploading: false,
          statusMessage: `Upload cancelled — ${prev.completedItems} photo${prev.completedItems !== 1 ? 's' : ''} saved`
        }));
        setIsCancelling(false);
      } else {
        console.error('🔴 Progressive upload error | correlationId:', correlationId, uploadError);
        setError(`Upload failed: ${uploadError.message}`);
        setUploadProgress(prev => ({
          ...prev,
          isUploading: false,
          statusMessage: 'Upload failed'
        }));
      }
    }
  };

  // Cancel an in-progress upload via dedicated cancel endpoint
  const handleCancelUpload = async () => {
    setIsCancelling(true);
    const jobId = currentJobIdRef.current;
    const API_BASE_URL = getGooglePhotosApiUrl();

    if (jobId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_BASE_URL}/cancel/${jobId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Backend will now send a 'cancelled' SSE event; stream reader will handle UI update
      } catch (err) {
        console.warn('Cancel API call failed, aborting stream directly', err);
        if (abortControllerRef.current) abortControllerRef.current.abort();
      }
    } else {
      // No job ID yet (upload hasn't fully started), fall back to aborting the fetch
      if (abortControllerRef.current) abortControllerRef.current.abort();
    }
  };

  // Clear auth and retry
  const handleClearAuth = () => {
    googlePhotosService.clearAuth();
    setError(null);
    console.log('🔄 Authentication cleared');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PhotoLibrary />
          Import from Google Photos
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Info section */}
          <Alert severity="info" icon={<Info />}>
            <Typography variant="body2">
              Import photos directly from your Google Photos library. 
              This will open Google Photos in a new window where you can select photos to import.
            </Typography>
          </Alert>

          {!googleClientIdDiagnostics.resolvedValue && (
            <Alert severity="warning">
              <Typography variant="body2">
                Google Photos runtime configuration is missing. Resolved source: {googleClientIdDiagnostics.source}. Local development expects REACT_APP_GOOGLE_CLIENT_ID in frontend env files plus a frontend restart. Deployed frontend expects REACT_APP_GOOGLE_CLIENT_ID or GOOGLE_CLIENT_ID in the container runtime environment so runtime-config.js can expose it.
              </Typography>
            </Alert>
          )}

          {/* Error display */}
          {error && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Button 
                size="small" 
                onClick={handleClearAuth}
                variant="outlined"
              >
                Clear Auth & Retry
              </Button>
            </Alert>
          )}

          {/* Progress Bar - Show during upload */}
          {uploadProgress.isUploading && (
            <Alert severity="info" icon={<CloudDownload />}>
              <Typography variant="body2" gutterBottom>
                {uploadProgress.statusMessage || 'Uploading photos...'}
              </Typography>
              <LinearProgress 
                variant={uploadProgress.totalItems > 0 ? "determinate" : "indeterminate"} 
                value={uploadProgress.totalItems > 0 ? (uploadProgress.completedItems / uploadProgress.totalItems) * 100 : 0}
                sx={{ mb: 1 }}
              />
              {uploadProgress.totalItems > 0 && (
                <Typography variant="body2" color="text.secondary" align="center">
                  {uploadProgress.completedItems} of {uploadProgress.totalItems} photos uploaded
                  {uploadProgress.currentItem && ` • Processing: ${uploadProgress.currentItem}`}
                </Typography>
              )}
            </Alert>
          )}

          {/* Thumbnail grid — grows as each photo completes */}
          {completedThumbnails.length > 0 && (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress.isUploading
                    ? `Imported ${completedThumbnails.length} of ${uploadProgress.totalItems}`
                    : `${completedThumbnails.length} photo${completedThumbnails.length !== 1 ? 's' : ''} imported`}
                </Typography>
                {!uploadProgress.isUploading && uploadProgress.errors?.length > 0 && (
                  <Chip label={`${uploadProgress.errors.length} failed`} size="small" color="error" />
                )}
                {!uploadProgress.isUploading && uploadProgress.skipped?.length > 0 && (
                  <Chip label={`${uploadProgress.skipped.length} skipped`} size="small" color="warning" sx={{ ml: 0.5 }} />
                )}
              </Box>
              <ImageList
                cols={5}
                gap={4}
                sx={{ maxHeight: 240, overflowY: 'auto', m: 0 }}
              >
                {completedThumbnails.map((thumb, idx) => (
                  <Tooltip key={idx} title={thumb.filename} placement="top" arrow>
                    <ImageListItem
                      sx={{
                        position: 'relative',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor:
                          thumb.status === 'completed' ? 'success.light'
                          : thumb.status === 'skipped'   ? 'warning.light'
                          : 'error.light'
                      }}
                    >
                      <AuthenticatedImage
                        imageId={thumb.imageId}
                        type="thumbnail"
                        alt={thumb.filename}
                        sx={{ width: '100%', height: 72, objectFit: 'cover', display: 'block' }}
                      />
                      {/* Status icon overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 2,
                          right: 2,
                          bgcolor: 'rgba(0,0,0,0.55)',
                          borderRadius: '50%',
                          display: 'flex',
                          p: '1px'
                        }}
                      >
                        {thumb.status === 'completed' && (
                          <CheckCircleOutline sx={{ fontSize: 14, color: 'success.light' }} />
                        )}
                        {thumb.status === 'skipped' && (
                          <SkipNext sx={{ fontSize: 14, color: 'warning.light' }} />
                        )}
                        {thumb.status === 'error' && (
                          <ErrorOutline sx={{ fontSize: 14, color: 'error.light' }} />
                        )}
                      </Box>
                    </ImageListItem>
                  </Tooltip>
                ))}
              </ImageList>
            </Box>
          )}

          {/* Loading state */}
          {loading && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={3}>
              <CircularProgress />
              <Typography variant="body2" color="textSecondary">
                {isPolling ? 'Waiting for photo selection...' : 'Initializing Google Photos...'}
              </Typography>
              {importProgress && (
                <Typography variant="body2" color="primary">
                  {importProgress}
                </Typography>
              )}
            </Box>
          )}

          {/* Selected photos preview */}
          {selectedPhotos.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Selected Photos ({selectedPhotos.length})
              </Typography>
              <List dense>
                {selectedPhotos.slice(0, 5).map((photo, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={photo.mediaFile?.filename || `Photo ${index + 1}`}
                      secondary={`${(photo.mediaFile?.sizeBytes / 1024 / 1024).toFixed(2)} MB`}
                    />
                  </ListItem>
                ))}
                {selectedPhotos.length > 5 && (
                  <ListItem>
                    <ListItemText
                      primary={`+ ${selectedPhotos.length - 5} more photos`}
                      sx={{ fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {/* Instructions when not loading and no photos selected */}
          {!loading && selectedPhotos.length === 0 && !error && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Ready to import photos from Google Photos
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <OpenInNew />
                  </ListItemIcon>
                  <ListItemText
                    primary="Click 'Open Google Photos' to start"
                    secondary="A new window will open where you can select photos"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhotoLibrary />
                  </ListItemIcon>
                  <ListItemText
                    primary="Select your photos"
                    secondary="Choose the photos you want to import to your collection"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloudDownload />
                  </ListItemIcon>
                  <ListItemText
                    primary="Photos will be imported"
                    secondary="Selected photos will be downloaded and added to your upload queue"
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button
          onClick={uploadProgress.isUploading ? handleCancelUpload : onClose}
          disabled={isCancelling}
          color={uploadProgress.isUploading ? 'error' : 'inherit'}
        >
          {isCancelling ? 'Cancelling…' : uploadProgress.isUploading ? 'Cancel Upload' : 'Close'}
        </Button>
        <Button
          onClick={handleTestConnection}
          variant="outlined"
          disabled={loading || uploadProgress.isUploading}
          startIcon={<CheckCircle />}
        >
          Test Connection
        </Button>
        <Button
          onClick={handleOpenPhotoPicker}
          variant="contained"
          disabled={loading || uploadProgress.isUploading}
          startIcon={loading ? <CircularProgress size={20} /> : <PhotoLibrary />}
        >
          {loading ? 'Opening...' : 'Open Google Photos'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GooglePhotosDialog;